/** The asset page's Technical tab: what ffprobe found in the original, and the proxy. */
import { formatDuration, frameRateLabel, type AssetDetail } from '@prism/contracts';
import { bitrate, bytes, dateTime, fps, resolution } from '../lib/format';

export function TechnicalTab({ asset }: { asset: AssetDetail }) {
  const t = asset.technical;
  if (!t) return <p className="muted">Technical metadata appears once ffprobe has inspected the original.</p>;
  const proxy = asset.representations.find((r) => r.kind === 'proxy');
  // 1.2.0: this tab describes the primary master; the others are on the Masters tab.
  const further = (asset.masters ?? []).filter((m) => !m.primary).length;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {further > 0 && <p className="muted" style={{ margin: 0, fontSize: 12.5 }} data-testid="technical-primary-note">The primary master’s technical metadata. {further} further master{further === 1 ? ' is' : 's are'} on the Masters tab.</p>}
      <div>
        <p className="section-label">Container</p>
        <dl className="kv">
          <dt>Format</dt><dd>{t.containerLongName ?? t.container}</dd>
          <dt>Duration</dt><dd className="mono">{formatDuration(t.durationTicks)} <span className="faint">({t.durationTicks.toLocaleString()} ticks)</span></dd>
          <dt>Size</dt><dd>{bytes(t.sizeBytes)}</dd>
          <dt>Bit rate</dt><dd>{bitrate(t.bitRate)}</dd>
          <dt>Start timecode</dt><dd className="mono">{t.startTimecode ?? 'None (00:00:00:00 assumed)'}</dd>
          <dt>Created</dt><dd>{t.creationTime ? dateTime(t.creationTime) : '—'}</dd>
        </dl>
      </div>
      <div>
        <p className="section-label">Timing</p>
        <dl className="kv">
          <dt>Source rate</dt><dd>{t.frameRate ? `${frameRateLabel(t.frameRate)} fps (${t.frameRate.num}/${t.frameRate.den})` : '—'}{t.variableFrameRate && <span className="pill" style={{ marginLeft: 6 }}>Variable</span>}</dd>
          <dt>Proxy rate</dt><dd>{fps(asset.timing.proxyFrameRate)}{asset.timing.normalizedFromVfr ? ' · normalised to constant' : ''}</dd>
          <dt>Preview</dt><dd>Approximate in the browser. Exports use authoritative timing.</dd>
        </dl>
      </div>
      <div>
        <p className="section-label">Streams</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {t.streams.map((s) => (
            <div key={s.index} className="rep-card">
              <div className="rep-head"><span className="pill accent">#{s.index} {s.type}</span><strong>{s.codec ?? 'unknown'}</strong>{s.profile && <span className="muted">{s.profile}</span>}{s.language && <span className="pill">{s.language}</span>}</div>
              <dl className="kv">
                {s.type === 'video' && <><dt>Picture</dt><dd>{resolution(s.width ?? null, s.height ?? null)}{s.pixelFormat ? ` · ${s.pixelFormat}` : ''}</dd><dt>Frame rate</dt><dd>{fps(s.frameRate)}{s.variableFrameRate ? ` (avg ${fps(s.averageFrameRate)})` : ''}</dd></>}
                {s.type === 'audio' && <><dt>Sample rate</dt><dd>{s.sampleRate ? `${(s.sampleRate / 1000).toFixed(1)} kHz` : '—'}</dd><dt>Channels</dt><dd>{s.channels ?? '—'}{s.channelLayout ? ` · ${s.channelLayout}` : ''}</dd></>}
                <dt>Bit rate</dt><dd>{bitrate(s.bitRate, 'kb')}</dd>
              </dl>
            </div>
          ))}
        </div>
      </div>
      {proxy && (
        <div>
          <p className="section-label">Proxy</p>
          <dl className="kv">
            <dt>Codec</dt><dd>{String(proxy.attributes.videoCodec ?? '—')} / {String(proxy.attributes.audioCodec ?? '—')}</dd>
            <dt>Picture</dt><dd>{resolution((proxy.attributes.width as number) ?? null, (proxy.attributes.height as number) ?? null)}</dd>
            <dt>Preset</dt><dd>{proxy.preset}</dd>
          </dl>
        </div>
      )}
    </div>
  );
}
